import { ColumnProps } from "antd/lib/table";
import { useTranslation } from "react-i18next";
import { IProductsOrder } from "../../../models/order/products-order/response";
import { ImCheckmark, ImCross } from "react-icons/im";
import Table from "../../general/table-components/table";
import { notification, theme, Table as AntTable } from "antd";
import { useState, useMemo } from "react";
import EditProductModal from "../product-management/EditProductModal";
import { Permissions } from "../../../utils/Permissions/Permissions";
import OrderProductService from "../../../services/order-product";
import { httpclient } from "../../../services/http-client";
import EditBtn from "../../general/table-components/actions/edit-btn";
import DeleteBtn from "../../general/table-components/actions/delete-btn";
import Space from "../../general/antd/space";

interface IProps {
  data: any[];
  orderId: number;
  onRefresh: () => void;
}

const ProductsOrderTable: React.FC<IProps> = ({ data, orderId, onRefresh }) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const userPermissions = localStorage.getItem("permisionStoroge")?.split(",") || [];

  // Check if any product has location change data
  const hasLocationChange = useMemo(() => {
    return data?.some((product: any) => product?.browsingPriceOnClient != null);
  }, [data]);

  const handleDelete = async (productOrderId: number) => {
    if (!productOrderId) {
      notification.error({
        message: t("error"),
        description: t("product_id_missing"),
      });
      return;
    }

    setLoading(true);
    try {
      const orderProductService = new OrderProductService(httpclient);
      const requestData = { productOrderId };
      await orderProductService.removeProduct(requestData);
      notification.success({
        message: t("success"),
        description: t("product_removed_successfully"),
      });
      onRefresh();
    } catch (error: any) {
      console.error("Error removing product:", error);
      const errorMessage = error?.response?.data?.message || error?.message || t("failed_to_remove_product");
      notification.error({
        message: t("error"),
        description: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (record: any) => {
    setSelectedProduct(record);
    setEditModalVisible(true);
  };

  const columns: ColumnProps<IProductsOrder>[] = [
    {
      title: t("name"),
      dataIndex: "name",
      key: "name",
      align: "center",
      minWidth: 120,
      render: (_, record) => {
        return record?.product?.name;
      },
    },
    {
      title: t("unit"),
      dataIndex: "unit",
      key: "unit",
      align: "center",
      minWidth: 100,
      render: (_, record) => {
        return record?.product?.unit?.name ?? "-";
      },
    },
    {
      title: (
        <span>
          {t("count")} * {t("unit_partition")} (
          <span style={{ color: "#b23d3dff", fontWeight: "bold" }}> {t("isRequired")} </span>)
        </span>
      ),
      dataIndex: "required_info",
      key: "required_info",
      align: "center",
      minWidth: 180,
      render: (_: any, record: IProductsOrder) => {
        const count = record?.baseCount ?? record?.count ?? 0;
        const multiplier = record?.baseUnitPartition
          ? Number(record?.baseUnitPartition?.percentage) / 100
          : 1;
        return (
          <div className="flex justify-center items-center" style={{ gap: "4px" }}>
            <span>{count}</span>
            <span> * </span>
            <span>{multiplier}</span>
          </div>
        );
      },
    },
    {
      title: (
        <span>
          {t("count")} * {t("unit_partition")} (
          <span style={{ color: "#16a34a", fontWeight: "bold" }}> {t("sent")} </span>)
        </span>
      ),
      dataIndex: "sent_info",
      key: "sent_info",
      align: "center",
      minWidth: 180,
      render: (_: any, record: IProductsOrder) => {
        const count = record?.count ?? 0;
        const multiplier = record?.unitPartition
          ? Number(record?.unitPartition?.percentage) / 100
          : 1;
        return (
          <div className="flex justify-center items-center" style={{ gap: "4px" }}>
            <span>{count}</span>
            <span> * </span>
            <span>{multiplier}</span>
          </div>
        );
      },
    },

    {
      title: t("single_selling_price"),
      dataIndex: "single_selling_price",
      key: "single_selling_price",
      align: "center",
      minWidth: 150,
      render: (_, record) => {
        return record?.priceOnClient ?? "-";
      },
    },
    // Location change columns - only shown when there's location change data
    ...(hasLocationChange ? [
      {
        title: t("browsing_price"),
        dataIndex: "browsingPriceOnClient",
        key: "browsingPriceOnClient",
        align: "center" as const,
        minWidth: 150,
        render: (_: any, record: IProductsOrder) => {
          return record?.browsingPriceOnClient?.toLocaleString() ?? "-";
        },
      },
      {
        title: t("product_price_difference"),
        dataIndex: "productPriceDifference",
        key: "productPriceDifference",
        align: "center" as const,
        minWidth: 150,
        render: (_: any, record: IProductsOrder) => {
          if (record?.productPriceDifference == null) return "-";
          const diff = record.productPriceDifference;
          return (
            <span style={{
              color: diff > 0 ? token.colorError : diff < 0 ? token.colorSuccess : token.colorText,
              fontWeight: 600,
            }}>
              {diff > 0 ? "+" : ""}{diff.toLocaleString()}
            </span>
          );
        },
      },
    ] : []),
    {
      title: t("total_selling_price"),
      dataIndex: "total_selling_price",
      key: "total_selling_price",
      align: "center",
      minWidth: 150,
      render: (_, record) => {
        return record?.totalPriceOnClient ?? "-";
      },
    },
    {
      title: t("store"),
      dataIndex: "store",
      key: "store",
      align: "center",
      render: (_, record) => {
        return record?.store?.name ?? "-";
      },
    },
    {
      title: t("accepted"),
      dataIndex: "isAccepted",
      align: "center",
      minWidth: 110,
      key: "isAccepted",
      render: (_, record) => {
        return (
          <>
            {record.isAccepted ? (
              <ImCheckmark color="green" />
            ) : (
              <ImCross color="red" />
            )}
          </>
        );
      },
    },
    {
      title: t("single_pharmacy_price"),
      dataIndex: "price",
      key: "price",
      align: "center",
      minWidth: 150,
      render: (_, record) => {
        return record?.price ?? "-";
      },
    },
    {
      title: t("alternative_product"),
      dataIndex: "alternative_product",
      key: "alternative_product",
      align: "center",
      minWidth: 100,
      render: (_, record) => {
        return record?.alternativeProduct?.name ?? "-";
      },
    },
    {
      title: t("nameSlice"),
      dataIndex: "nameSlice",
      key: "nameSlice",
      align: "center",
      minWidth: 100,
      render: (_, record) => {
        return record?.nameSlice ?? "-";
      },
    },
    {
      title: t("numberPoint"),
      dataIndex: "numberPoint",
      key: "numberPoint",
      align: "center",
      minWidth: 100,
      render: (_, record) => {
        return record?.numberPoint ?? "-";
      },
    },
    {
      title: t("need_recipe"),
      dataIndex: "recipeRequired",
      align: "center",
      minWidth: 110,
      key: "recipeRequired",
      render: (_, record) => {
        return (
          <>
            {record.recipeRequired ? (
              <ImCheckmark color="green" />
            ) : (
              <ImCross color="red" />
            )}
          </>
        );
      },
    },

    {
      title: t("actions"),
      width: 150,
      dataIndex: "",
      align: "center",
      fixed: "right",
      key: "x",
      render: (_: any, record: any) => {
        return (
          <Space>
            {userPermissions.includes(Permissions.CAN_ADMIN_UPDATE_ORDER_PRODUCT_QUANTITY) && (
              <EditBtn
                onClick={() => handleEdit(record)}
                aria-label={`${t("edit")} ${record?.product?.name}`}
              />
            )}
            {userPermissions.includes(Permissions.CAN_ADMIN_REMOVE_ORDER_PRODUCT) && (
              <DeleteBtn
                onConfirm={() => {
                  handleDelete(record.id);
                }}
                loading={loading}
                aria-label={`${t("delete")} ${record?.product?.name}`}
              />
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <Table<any>
        rowKey="id"
        columns={columns}
        dataSource={data ?? []}
        size="small"
        scroll={{ y: data && data.length > 10 ? 400 : undefined, x: "max-content" }}
        expandable={{
          rowExpandable: (record) => !!record.batchWithdrawals?.length,
          expandedRowRender: (record) => (
            <AntTable
              rowKey={(_, i) => String(i)}
              size="small"
              pagination={false}
              dataSource={record.batchWithdrawals}
              columns={[
                {
                  title: t("batch_number"),
                  dataIndex: "batchNumber",
                  key: "batchNumber",
                  render: (val: string, row: any) => val ?? `#${row.batchId}`,
                },
                {
                  title: t("expiration_date"),
                  dataIndex: "expirationDate",
                  key: "expirationDate",
                  render: (val: string) => val ?? "-",
                },
                {
                  title: t("withdrawn_quantity"),
                  dataIndex: "quantity",
                  key: "quantity",
                },
              ]}
            />
          ),
        }}
      />

      {selectedProduct && (
        <EditProductModal
          visible={editModalVisible}
          onCancel={() => {
            setEditModalVisible(false);
            setSelectedProduct(null);
          }}
          onSuccess={() => {
            setEditModalVisible(false);
            setSelectedProduct(null);
            onRefresh();
          }}
          orderId={orderId}
          orderProduct={selectedProduct}
        />
      )}
    </>
  );
};

export default ProductsOrderTable;
